/**
 * Renders a phone number search widget to be used at the top of the search bar
 * or at the side with the other widgets to allow the user to search calls by
 * phone numbers
 */
import React from 'react';
import ReactFlagsSelect from 'react-flags-select';
import 'react-flags-select/css/react-flags-select.css';
import classNames from 'classnames';

import './PhoneNumberSearchWidget.styl';
import isNumber from 'utils/number/isNumber';
import appendDashToPhonePart from 'utils/phone/appendDashToPhonePart';
import isPhoneNumber, {phoneNumberRegex} from 'utils/phone/isPhoneNumber';
import {
  COUNTRY_CODE_TO_NATIONAL_NUMBER,
  COUNTRY_CODE_TO_PHONE_PARTS,
  DEFAULT_COUNTRY,
} from 'components/pure/utils/phone/const';

export const PHONE_NUMBER_FLEX_DIV_SIDEBAR_CSS_NAME = 'search-input-phone-sidebar-flex-div';

/**
 * Render a phone number search widget where the country, area, part1, and part2
 * are rendered in separate components
 */
export default class PhoneNumberSearchWidget extends React.PureComponent {
  constructor() {
    super();
    this.state = {
      country: DEFAULT_COUNTRY,
      area: this._defaultArea(),
      part1: this._defaultPart1(),
      part2: this._defaultPart2(),
    };
  }

  /**
   * If this widget is mounted the first time after the user has already typed
   * some digits into the top search bar, we parse the digits into components
   * (area/part1/part2) and put those components' digits into corresponding
   * fields.
   */
  componentDidMount() {
    if (this.props.isRenderedInTopBar && isPhoneNumber(this.props.searchTerm)) {
      this.setState(this._parsePhoneNumber(this.props.searchTerm), () =>
        this.props.onSearchTermChange(this._searchTermFromPhoneParts())
      );
    }
  }

  handleAreaBlur = event => {
    this._handleBlur(event, 'area');
  };

  handlePart1Blur = event => {
    this._handleBlur(event, 'part1');
  };

  handlePart2Blur = event => {
    this._handleBlur(event, 'part2');
  };

  handleAreaChange = event => {
    this._handleChange(event, 'area');
  };

  handlePart1Change = event => {
    this._handleChange(event, 'part1');
  };

  handlePart2Change = event => {
    this._handleChange(event, 'part2');
  };

  handlePhoneNumberInputPaste = event => {
    const pastedValue = event.clipboardData?.getData('text')?.trim();
    if (isPhoneNumber(pastedValue)) {
      this.setState(this._parsePhoneNumber(pastedValue), () =>
        this.props.onSearchTermChange(this._searchTermFromPhoneParts())
      );
    }
  };

  /**
   * Parses the given string as a phone number into 3 parts. If the given string is an invalid
   * phone number, then return empty strings for each part.
   * @param phoneNumber {string} the string that represents a phone number
   * @private
   */
  _parsePhoneNumber(phoneNumber) {
    const result = phoneNumberRegex.exec(phoneNumber);
    return {
      area: result?.groups?.area ?? '',
      part1: result?.groups?.part1 ?? '',
      part2: result?.groups?.part2 ?? '',
    };
  }

  /**
   * Callback triggered whenever the user changes the country selected
   * @param countryCode {string} 2-chars country code
   * @private
   */
  _onSelectCountry = countryCode => {
    this.setState({country: countryCode});
    setTimeout(() => this.props.onSearchTermChange(this._searchTermFromPhoneParts()));
  };

  _defaultArea() {
    const country_phone_parts = COUNTRY_CODE_TO_PHONE_PARTS[DEFAULT_COUNTRY];
    return '-'.repeat(country_phone_parts.area);
  }

  _defaultPart1() {
    const country_phone_parts = COUNTRY_CODE_TO_PHONE_PARTS[DEFAULT_COUNTRY];
    return '-'.repeat(country_phone_parts.part1);
  }

  _defaultPart2() {
    const country_phone_parts = COUNTRY_CODE_TO_PHONE_PARTS[DEFAULT_COUNTRY];
    return '-'.repeat(country_phone_parts.part2);
  }

  /**
   * Handle a user's blur from one input field to the next via tab
   * @param event {object} event of the blur
   * @param inputField {string} input field that the user tabbed away
   * @private
   */
  _handleBlur(event, inputField) {
    const phonePartLength = this._phonePartLength(inputField);
    const eventValues = this._inputChangeEventValues(event.target.value, phonePartLength);
    if (this._isInputChangeValueValid(eventValues, phonePartLength)) {
      const state = {};
      state[inputField] = appendDashToPhonePart(eventValues.value, phonePartLength);

      this.setState(state);
    }
  }

  /**
   * Handle a user's change to the value of an input field
   * @param event {object} event of the change
   * @param inputField {string} input field that received the change
   * @private
   */
  _handleChange(event, inputField) {
    const phonePartLength = this._phonePartLength(inputField);

    let value = event.target.value;

    if (value.length > phonePartLength) return;

    if (event.nativeEvent.data !== '-') {
      value = trimDashInPhonePart(value);
    }
    const eventValues = this._inputChangeEventValues(value, phonePartLength);
    if (this._isInputChangeValueValid(eventValues, phonePartLength)) {
      const state = {};
      state[inputField] = value;

      this.setState(state, () => this.props.onSearchTermChange(this._searchTermFromPhoneParts()));
    }
    if (this._isInputChangeValueFull(eventValues, phonePartLength)) {
      const input = (() => {
        // eslint-disable-next-line
        switch (inputField) {
          case 'area':
            return this.part1Input;
          case 'part1':
            return this.part2Input;
          case 'part2':
            return this.area;
        }
      })();
      input && input.focus();
    }
  }

  _phonePartLength(inputField) {
    // eslint-disable-next-line
    switch (inputField) {
      case 'area':
        return this._areaPhonePartLength();
      case 'part1':
        return this._part1PhonePartLength();
      case 'part2':
        return this._part2PhonePartLength();
    }
  }

  /**
   * Return the `searchTerm` that will be passed to different stores to
   * represent a phone number wildcard search by combining the phone parts
   * values into 1 wildcard `searchTerm`
   *
   * converts whitespace to `-` up to the amount necessary given the
   * country code / phone part
   *
   * @private
   */
  _searchTermFromPhoneParts() {
    const prefix = COUNTRY_CODE_TO_NATIONAL_NUMBER[this.state.country].code;
    const area = replaceDashToStarInPhonePart(
      this.state.area.padEnd(this._areaPhonePartLength(), '-')
    );
    const part1 = replaceDashToStarInPhonePart(
      this.state.part1.padEnd(this._part1PhonePartLength(), '-')
    );
    const part2 = replaceDashToStarInPhonePart(
      this.state.part2.padEnd(this._part2PhonePartLength(), '-')
    );
    let searchTerm = prefix + area + part1 + part2;
    if (
      !partContainsAnyDigits(this.state.area) &&
      !partContainsAnyDigits(this.state.part1) &&
      partContainsAnyDigits(this.state.part2)
    ) {
      // Edge case: Some BrandRep calls contain a phone number that has only
      // 4-digits which is the extension of a full phone number. For those
      // calls, we search by `*part2` to find them while ignoring the country
      // prefix
      searchTerm = '*' + part2;
    }

    return searchTerm;
  }

  /**
   * Return true if eventValues.value and .nonPlaceholder are valid phone digits
   * @param eventValues {object}
   * @param maxNumDigits {number}
   * @return {boolean}
   * @private
   */
  _isInputChangeValueValid(eventValues, maxNumDigits) {
    return (
      eventValues.value.length <= maxNumDigits &&
      (eventValues.nonPlaceholder === '' || isNumber(eventValues.nonPlaceholder))
    );
  }

  /**
   * Return true if eventValues.value is equal to maxNumDigits so we know the
   * user has typed all the digits available in the current <input/> field
   * @param eventValues {object}
   * @param maxNumDigits {number}
   * @return {boolean}
   * @private
   */
  _isInputChangeValueFull(eventValues, maxNumDigits) {
    return eventValues.nonPlaceholder.length === maxNumDigits;
  }

  /**
   * Return an object ot encapsulate the nonPlaceholder and value from an
   * input.event object
   * @param value {string}
   * @param maxNumDigits {number}
   * @return {{nonPlaceholder: String, value: String}}
   * @private
   */
  _inputChangeEventValues(value, maxNumDigits) {
    if (value.length > maxNumDigits) {
      value = value.substring(0, maxNumDigits);
    }
    const nonPlaceholderValue = value.replace(/-/g, '');
    return {
      value: value,
      nonPlaceholder: nonPlaceholderValue,
    };
  }

  /**
   * Return the current country's area phone part length
   * @private
   */
  _areaPhonePartLength() {
    const phoneParts = COUNTRY_CODE_TO_PHONE_PARTS[this.state.country];
    if (phoneParts) {
      // Use this.state.country's area-code part length
      return phoneParts.area;
    } else {
      // Fallback to US-area-code part length if this.state.country is not
      // supported
      return COUNTRY_CODE_TO_PHONE_PARTS[DEFAULT_COUNTRY].area;
    }
  }

  /**
   * Return the current country's part1 phone part length
   * @private
   */
  _part1PhonePartLength() {
    const phoneParts = COUNTRY_CODE_TO_PHONE_PARTS[this.state.country];
    if (phoneParts) {
      return phoneParts.part1;
    } else {
      return COUNTRY_CODE_TO_PHONE_PARTS[DEFAULT_COUNTRY].part1;
    }
  }

  /**
   * Return the current country's part2 phone part length
   * @private
   */
  _part2PhonePartLength() {
    const phoneParts = COUNTRY_CODE_TO_PHONE_PARTS[this.state.country];
    if (phoneParts) {
      return phoneParts.part2;
    } else {
      return COUNTRY_CODE_TO_PHONE_PARTS[DEFAULT_COUNTRY].part2;
    }
  }

  _renderPhoneNumberLabel(label) {
    return (
      <div className={'phone-number-label'}>
        <span>{label}</span>
      </div>
    );
  }

  /**
   * Render 4 separate, grouped components for country, area, part1, and part2
   */
  render() {
    const flexDivClassname = this.props.flexDivClassname;
    const inputClassnames = classNames([
      'search-input',
      'search-input-phone-number',
      'search-input-phone-number-area',
    ]);
    const inputPart1Classnames = classNames([
      'search-input',
      'search-input-phone-number',
      'search-input-phone-number-part1',
    ]);
    const inputPart2Classnames = classNames([
      'search-input',
      'search-input-phone-number',
      'search-input-phone-number-part2',
    ]);
    const reactFlagSelectClassnames = classNames([
      flexDivClassname === PHONE_NUMBER_FLEX_DIV_SIDEBAR_CSS_NAME
        ? 'phone-number-react-flag-select'
        : null,
    ]);
    const showInputLabel = flexDivClassname === PHONE_NUMBER_FLEX_DIV_SIDEBAR_CSS_NAME;
    const countryLabelClassnames = classNames(['phone-number-label', 'country-label']);
    return (
      <div className={classNames(this.props.className, 'ph-search-wrapper')}>
        <div className={flexDivClassname}>
          {showInputLabel ? <span className={countryLabelClassnames}>Country</span> : null}
          <ReactFlagsSelect
            onSelect={this._onSelectCountry}
            className={reactFlagSelectClassnames}
            defaultCountry={'US'}
            showSelectedLabel={false}
            searchable={true}
          />
        </div>
        <div className={flexDivClassname}>
          <input
            ref={ref => (this.areaInput = ref)}
            tabIndex={1}
            className={inputClassnames}
            onFocus={() => this.state.area === this._defaultArea() && this.setState({area: ''})}
            onBlur={this.handleAreaBlur}
            onChange={this.handleAreaChange}
            value={this.state.area}
            onPaste={this.handlePhoneNumberInputPaste}
          />
          {showInputLabel ? this._renderPhoneNumberLabel('Area') : null}
        </div>
        <div className={flexDivClassname}>
          <input
            tabIndex={2}
            ref={ref => (this.part1Input = ref)}
            className={inputPart1Classnames}
            onFocus={() => this.state.part1 === this._defaultPart1() && this.setState({part1: ''})}
            onBlur={this.handlePart1Blur}
            onChange={this.handlePart1Change}
            value={this.state.part1}
            onPaste={this.handlePhoneNumberInputPaste}
          />
          {showInputLabel ? this._renderPhoneNumberLabel('Prefix') : null}
        </div>
        <div className={flexDivClassname}>
          <input
            tabIndex={3}
            ref={ref => (this.part2Input = ref)}
            className={inputPart2Classnames}
            onFocus={() => this.state.part2 === this._defaultPart2() && this.setState({part2: ''})}
            onBlur={this.handlePart2Blur}
            onChange={this.handlePart2Change}
            value={this.state.part2}
            onPaste={this.handlePhoneNumberInputPaste}
          />
          {showInputLabel ? this._renderPhoneNumberLabel('Line') : null}
        </div>
      </div>
    );
  }
}

/**
 * Return true if a phone number part contains any digits, false if not. This is
 * used to tell us whether the user provided an edge case where only part2
 * contains digits among `area, part1, and part2`
 */
function partContainsAnyDigits(part) {
  return /\d/.test(part);
}

/**
 * Replace all - strings in a phone part with *
 * @param part {string}
 *
 * Examples:
 *
 * >> '--1--'.replace(/-/g, "*")
 * < "**1**"
 * >> '1--'.replace(/-/g, "*")
 * < "1**"
 */
function replaceDashToStarInPhonePart(part) {
  return part.replace(/-/g, '*');
}

/**
 * Remove dashes at the start and end of `value`
 * @param value {string}
 * @return {*}
 */
function trimDashInPhonePart(value) {
  return value.replace(new RegExp('^[-]+'), '').replace(new RegExp('[-]+$'), '');
}
